import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Card, Button, Table, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { GetAllDietPlanByUser } from '../../../../core/store/diet/dietPan/thunk';
import { createSelector } from "@reduxjs/toolkit";
import EnhancedDietPlan from './EnhancedDietPlan';
import Loading from '../../../components/loading';
import { useNotification } from "../../../../context/NotificationContext";
import moment from "moment";
import { ToastContainer } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import "../../../../css/agenda.css";
import { Utensils, Calendar, Plus } from 'lucide-react';
const MealPlan = () => {
  // États
  const [nextDailyPlan, setNextDailyPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Hooks
  const { showNotification } = useNotification();
  const hasNotified = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Sélecteur Redux optimisé
  const mealPlanData = createSelector(
    (state) => state.DietPlan,
    (dietPlan) => ({
      error: dietPlan.error,
      loading: dietPlan.loading,
      mealPlans: dietPlan.mealPlans || [],
    })
  );

  const { error, loading, mealPlans } = useSelector(mealPlanData);

  // Chargement des plans de repas
  const fetchMealPlans = useCallback(async () => {
    const userId = JSON.parse(sessionStorage.getItem('authUser'))?.data?.userId;
    if (!userId) {
      console.error('User ID not found in sessionStorage');
      return;
    }

    try {
      setIsLoading(true);
      const result = await dispatch(GetAllDietPlanByUser(userId)).unwrap();
      const plans = result?.data || [];
      const todayDate = moment().format("YYYY-MM-DD");
      
      // Trouver le plan du jour
      const todayPlan = plans.flatMap(plan => 
        plan.dailyPlans?.filter(daily => daily.dailyDate === todayDate) || []
      )[0];

      setNextDailyPlan(todayPlan || null);
    } catch (error) {
      console.error('Erreur lors du chargement des plans:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  // Gestion des notifications
  const handleMealNotifications = useCallback((dailyPlan) => {
    if (!dailyPlan?.dailyMeals?.length || hasNotified.current) return;
    
    hasNotified.current = true;
    const consumedCount = dailyPlan.dailyMeals.filter(meal => meal.consumed).length;
    const totalMeals = dailyPlan.dailyMeals.length;

    const notifications = [
      { condition: consumedCount === 0, 
        message: "⚠️ Aucun repas consommé aujourd'hui. Essayez de rester sur la bonne voie !", 
        type: "warn" },
      { condition: consumedCount === totalMeals, 
        message: "🎉 Félicitations ! Vous avez atteint vos objectifs journaliers !", 
        type: "success" },
      { condition: consumedCount === 1, 
        message: "🍽️ Vous avez consommé un repas. Continuez ainsi !", 
        type: "success" },
      { condition: consumedCount === 2, 
        message: "✅ Deux repas consommés. Vous êtes sur la bonne voie !", 
        type: "success" }
    ];

    const notification = notifications.find(n => n.condition) || {
      message: `🍴 Vous avez consommé ${consumedCount} repas. Continuez !`,
      type: "success"
    };

    showNotification(notification.message, notification.type, {
      background: notification.type === "warn" ? "#413c26" : "#2E7D32",
      color: "#fff"
    });
  }, [showNotification]);


  // Effets
  useEffect(() => {
    const loadData = async () => {
      await fetchMealPlans();
    };
    loadData();
  }, [fetchMealPlans]);

  useEffect(() => {
    if (nextDailyPlan) {
      handleMealNotifications(nextDailyPlan);
    }
  }, [nextDailyPlan, handleMealNotifications]);

  // Gestion du chargement et des erreurs
  if (isLoading || loading) return <Loading />;
  if (error) return (
    <div className="alert alert-danger m-3">
      Erreur lors du chargement des plans de repas: {error.message || 'Erreur inconnue'}
    </div>
  );
   
  // Rendu d'un seul plan journalier
  const renderDailyPlan = (daily) => (
    <div key={daily.dailyDate} className="mb-4">
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-success bg-opacity-10 d-flex align-items-center py-3">
          <Utensils className="me-2" size={20} />
          <h5 className="mb-0 text-success">
            Planification du {new Date(daily.dailyDate).toLocaleDateString("fr-FR", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric"
            })}
          </h5>
          <Badge bg="success" className="ms-auto">
            {daily.dailyMeals?.length || 0} repas prévus
          </Badge>
        </Card.Header>

        <Card.Body>
          <div className="mb-4">
            <h6 className="text-muted mb-3">Objectifs nutritionnels</h6>
            <Table responsive hover className="align-middle">
              <thead className="table-light">
                <tr>
                  <th>Calories</th>
                  <th>Protéines</th>
                  <th>Lipides</th>
                  <th>Glucides</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="fw-bold">
                    <div className="d-flex flex-column">
                      <span>{daily.dailyGoal?.calories || 0} kcal</span>
                      <small className="text-muted">Objectif</small>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column">
                      <span className="fw-bold">{daily.dailyGoal?.protein || 0} g</span>
                      <small className="text-muted">Protéines</small>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column">
                      <span className="fw-bold">{daily.dailyGoal?.fats || 0} g</span>
                      <small className="text-muted">Lipides</small>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column">
                      <span className="fw-bold">{daily.dailyGoal?.carbs || 0} g</span>
                      <small className="text-muted">Glucides</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>

          <div className="meals-container">
            <h6 className="text-muted mb-3 d-flex align-items-center">
              <Utensils className="me-2" size={18} />
              Repas prévus
            </h6>
            
            {daily.dailyMeals?.length > 0 ? (
              <div className="row g-3">
                {daily.dailyMeals.map((meal, mealIndex) => (
                  <div key={`${daily.dailyDate}-${mealIndex}`} className="col-12">
                    <EnhancedDietPlan dailyMeal={meal} daily={daily} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-muted">
                <p className="mb-3">Aucun repas prévu pour cette journée</p>
                <Button variant="outline-primary" size="sm" onClick={() => navigate('/ajouter-repas')}>
                  <Plus size={16} className="me-1" /> Ajouter un repas
                </Button>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  return (
    <div className="mealplan-container">
      <ToastContainer position="top-right" autoClose={5000} />
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        
        <Button 
          variant="success" 
          onClick={() => navigate('/generer-plan')}
          className="d-flex align-items-center"
        >
          <Calendar size={18} className="me-1" />
          Voir l'agenda
        </Button>
        <h2 className="h4 mb-0 d-flex justify-content-center align-items-center ml-5">
          <Calendar className="me-2 text-primary" size={24} />
          Mon planning alimentaire
        </h2>
      </div>

      <div className="program-container">
        {mealPlans?.length > 0 ? (
          mealPlans.flatMap(plan => 
            plan.dailyPlans?.map(daily => renderDailyPlan(daily))
          )
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <div className="mb-3">
                <Utensils size={48} className="text-muted" />
              </div>
              <h4 className="mb-3">Aucun plan alimentaire trouvé</h4>
              <p className="text-muted mb-4">
                Commencez par créer votre premier plan alimentaire personnalisé
              </p>
              <Button 
                variant="primary" 
                size="lg" 
                onClick={() => navigate('/generer-plan')}
                className="px-4"
              >
                <Plus size={20} className="me-2" />
                Créer un plan
              </Button>
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
    
};

export default MealPlan;
